import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ErrorApi, fetchAutenticado } from '../api/cliente';
import type {
  EquipoAtendido,
  Opcion, OpcionTecnico, Orden, OrdenCandidata, Pagina, ProgramaPreventivo,
  PropuestaPreventiva, Refaccion, ServiciosPactados, SolicitudAnticipada, Sucursal,
  SucursalTrimestral, Trimestrales as TrimestralesRespuesta, VisitaPreventiva,
} from '../api/tipos';
import { useSesion } from '../sesion';
import { folioOT } from '../folio';
import { ETIQUETAS_ESTATUS, Ficha as FichaDeLaOrden } from './Ordenes';
import { BuscadorRefaccion } from '../componentes/BuscadorRefaccion';
import {
  Cargando, Dialogo, DialogoMotivo, Error, Faltantes, Resultados,
} from '../componentes/comunes';
import { aligerar } from '../imagenes';

/**
 * El programa preventivo del mes.
 *
 * Qué sucursales le tocan a cada técnico y si ya se hizo la visita. Es lo
 * que APRISA pidió: «cada técnico debe saber qué sucursales visitar en
 * ese mes».
 *
 * El programa no genera órdenes. El folio lo asigna Walmart y las
 * órdenes entran por la carga del archivo; aquí se marca la visita contra
 * la orden real cuando aparece. Es una agenda con acuse, y por eso lo que
 * la pantalla enseña primero es cuánto falta, no cuánto se hizo.
 */

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

/*
  Las etiquetas del programa ya no viven aquí.

  Decían sólo el estatus de la visita —«Realizada» en cuanto el técnico
  iba— y callaban en qué iba su orden, que es lo que decide si eso llega
  a facturación. Una visita hecha cuya refacción no se ha surtido decía
  «Realizada» y quien lo leía entendía que ya estaba.

  Ahora las arma la API en `preventivo/en-que-va.ts`, juntando los dos
  hechos, para que el programa, la ficha y lo que venga después digan lo
  mismo. El `estatus` que se guarda no cambió: la visita se hizo, y eso
  es un hecho de la visita.
*/

function fechaCorta(iso: string): string {
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

/**
 * Los equipos sobre los que se hizo un servicio.
 *
 * Sin importe a propósito: el precio es del renglón y no se multiplica
 * por equipo. Ponerle una cifra a cada uno haría creer que la visita se
 * cobra por aparato, que es justo lo contrario de lo pactado.
 *
 * No se enseña nada cuando no hay: una visita de antes de que existiera
 * la captura no está incompleta, sólo no lo registró.
 */
function ListaDeEquipos({ equipos }: { equipos: EquipoAtendido[] }) {
  if (equipos.length === 0) return null;

  return (
    <div className="ayuda" style={{ marginTop: 2 }}>
      Equipos: {equipos.map((e) => e.nombre).join(' · ')}
    </div>
  );
}

/**
 * Los técnicos de un desplegable, separados por zona.
 *
 * Cuando la lista viene acotada a una región, la API manda también los
 * que no tienen zona asignada: no son de otra parte, es que nadie los ha
 * clasificado, y dejarlos fuera los volvía imposibles de asignar. Van
 * agrupados para que se vea la diferencia en vez de mezclarse.
 */
function OpcionesDeTecnico({ tecnicos }: { tecnicos: OpcionTecnico[] }) {
  const conZona = tecnicos.filter((t) => t.zona);
  const sinZona = tecnicos.filter((t) => !t.zona);

  return (
    <>
      {conZona.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
      {sinZona.length > 0 && (
        <optgroup label="Sin zona asignada">
          {sinZona.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
        </optgroup>
      )}
    </>
  );
}

function Tarjeta({ titulo, valor, alerta }: {
  titulo: string; valor: number; alerta?: boolean;
}) {
  return (
    <div className="tarjeta" style={{ cursor: 'default' }}>
      <div className="conteo">{titulo}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: alerta ? '#b02a37' : undefined }}>
        {valor}
      </div>
    </div>
  );
}

/**
 * Región y estado, encadenados, para las dos vistas.
 *
 * Devuelve los valores y los dos desplegables ya armados. Las dos
 * pantallas preguntan por la misma zona y ninguna necesita saber cómo se
 * cargan: partirlo en un componente obligaría a declarar el mismo par de
 * estados en cada una, que es la duplicación que esto evita.
 */
function useZona() {
  const [regionId, setRegionId] = useState('');
  const [estadoId, setEstadoId] = useState('');
  const [regiones, setRegiones] = useState<Opcion[]>([]);
  const [estados, setEstados] = useState<Opcion[]>([]);

  useEffect(() => {
    api.get<Opcion[]>('/catalogos/regiones/opciones').then(setRegiones).catch(() => undefined);
  }, []);

  // Los estados se acotan a la región: es el escalón intermedio de la
  // jerarquía región → estado → sucursal.
  useEffect(() => {
    api.get<Opcion[]>('/catalogos/estados/opciones', { region_id: regionId || undefined })
      .then(setEstados)
      .catch(() => setEstados([]));
  }, [regionId]);

  const selectores = (
    <>
      {/* Al cambiar de zona se suelta el estado: uno de Yucatán con la
          región metro elegida no devuelve nada, y parece que no hay
          programa. */}
      <select
        value={regionId}
        onChange={(e) => { setRegionId(e.target.value); setEstadoId(''); }}
      >
        <option value="">Todas las zonas</option>
        {regiones.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
      </select>
      <select value={estadoId} onChange={(e) => setEstadoId(e.target.value)}>
        <option value="">
          {regionId ? 'Todos los estados de la zona' : 'Todos los estados'}
        </option>
        {estados.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
      </select>
    </>
  );

  return { regionId, estadoId, selectores };
}

/**
 * El preventivo, en dos vistas.
 *
 * El mes —quién va a qué sucursal ahora— y el año —cada cuándo le toca a
 * cada trimestral—. Son dos preguntas distintas sobre lo mismo y se
 * responden en momentos distintos: el año se acomoda una vez y el mes se
 * arma cada mes.
 */
export default function Preventivo() {
  const { puede } = useSesion();
  const [vista, setVista] = useState<'mes' | 'anio' | 'refacciones'>('mes');

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Programa preventivo</h1>
          <p className="sub">
            Qué sucursales le tocan a cada técnico este mes, y si ya se
            visitaron. El programa no crea órdenes: la visita se marca
            contra la orden preventiva que llega del cliente.
          </p>
        </div>
      </div>

      <nav className="submenu">
        <button className={vista === 'mes' ? 'activo' : ''} onClick={() => setVista('mes')}>
          El mes
        </button>
        <button className={vista === 'anio' ? 'activo' : ''} onClick={() => setVista('anio')}>
          El año de las trimestrales
        </button>
        {/* Las piezas que tardan más de treinta días se piden antes de
            que exista la orden, y aquí es donde se sabe qué visita viene. */}
        <button
          className={vista === 'refacciones' ? 'activo' : ''}
          onClick={() => setVista('refacciones')}
        >
          Refacciones anticipadas
        </button>
      </nav>

      {vista === 'mes' && <ProgramaDelMes />}
      {vista === 'anio' && <Trimestrales reparte={puede('preventivo', 'editar')} />}
      {vista === 'refacciones' && (
        <Anticipadas pide={puede('preventivo', 'editar')} />
      )}
    </>
  );
}

/**
 * Las refacciones que se piden antes de que exista la orden.
 *
 * Para una visita se sabe con semanas de anticipación qué va a hacer
 * falta, y hay que pedirlo con esa anticipación porque tarda más de
 * treinta días. La orden, en cambio, llega del cliente casi el mismo mes.
 *
 * Es una solicitud a almacén de verdad: almacén la revisa y la escala a
 * compras, compras la compra y la recibe. Lo único que espera a la orden
 * es la entrega, porque la salida del inventario se registra contra ella.
 */
function Anticipadas({ pide }: { pide: boolean }) {
  const [pagina, setPagina] = useState<Pagina<SolicitudAnticipada> | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [pidiendo, setPidiendo] = useState(false);
  const [enlazando, setEnlazando] = useState<SolicitudAnticipada | null>(null);

  const cargar = useCallback(() => {
    api.get<Pagina<SolicitudAnticipada>>('/solicitudes-almacen', {
      anticipadas: 'si',
      situacion: 'todas',
      busqueda: busqueda || undefined,
      limite: 50,
    })
      .then(setPagina)
      .catch(setError);
  }, [busqueda]);

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, busqueda]);

  return (
    <>
      <div className="panel">
        <div className="controles">
          <input
            type="search"
            placeholder="Folio, sucursal o determinante…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
          {pide && (
            <button className="primario" onClick={() => setPidiendo(true)}>
              Pedir refacciones
            </button>
          )}
        </div>

        <p className="ayuda">
          Almacén y compras las trabajan desde que se piden. Lo que espera
          es la entrega: la salida del inventario se registra contra una
          orden, así que se entregan cuando llegue la OT de esa visita y se
          le agreguen.
        </p>

        <Error error={error} />

        {!pagina ? <Cargando /> : pagina.data.length === 0 ? (
          <div className="vacio">
            No hay refacciones pedidas por anticipado.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Folio</th>
                <th>Sucursal</th>
                <th>Piezas</th>
                <th>Estatus</th>
                <th>Pedida</th>
                {pide && <th />}
              </tr>
            </thead>
            <tbody>
              {pagina.data.map((s) => (
                <tr key={s.id}>
                  <td className="mono">{s.folio}</td>
                  <td>
                    {s.sucursal
                      ? `${s.sucursal.determinante} — ${s.sucursal.nombre}`
                      : '—'}
                  </td>
                  <td>
                    {s.pendientes > 0
                      ? `${s.pendientes} de ${s.partidas} pendientes`
                      : `${s.partidas}`}
                  </td>
                  <td><span className="etiqueta">{s.estatus.replace('_', ' ')}</span></td>
                  <td>{fechaCorta(s.creadoEn)}</td>
                  {pide && (
                    <td>
                      <button className="enlace" onClick={() => setEnlazando(s)}>
                        Agregar a una orden
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {pidiendo && (
        <PedirAnticipado
          onCerrar={() => setPidiendo(false)}
          onHecho={() => { setPidiendo(false); cargar(); }}
        />
      )}

      {enlazando && (
        <AgregarAUnaOrden
          solicitud={enlazando}
          onCerrar={() => setEnlazando(null)}
          onHecho={() => { setEnlazando(null); cargar(); }}
        />
      )}
    </>
  );
}

/** Pedir piezas para una sucursal, sin orden todavía. */
function PedirAnticipado({ onCerrar, onHecho }: {
  onCerrar: () => void; onHecho: () => void;
}) {
  const [busqueda, setBusqueda] = useState('');
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [sucursalId, setSucursalId] = useState('');
  const [lineas, setLineas] = useState<{
    refaccion: Refaccion | null; descripcionLibre: string; cantidad: string;
  }[]>([{ refaccion: null, descripcionLibre: '', cantidad: '1' }]);
  const [notas, setNotas] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // Son 365 sucursales: se buscan, no se listan.
  useEffect(() => {
    if (busqueda.trim().length < 2) { setSucursales([]); return undefined; }
    const t = setTimeout(() => {
      api.get<Pagina<Sucursal>>('/sucursales', {
        busqueda: busqueda.trim(), estado: 'activo', limite: 20,
      })
        .then((r) => setSucursales(r.data))
        .catch(() => setSucursales([]));
    }, 300);
    return () => clearTimeout(t);
  }, [busqueda]);

  function cambiar(i: number, parcial: Partial<(typeof lineas)[number]>) {
    setLineas((antes) => antes.map((l, n) => (n === i ? { ...l, ...parcial } : l)));
  }

  const completas = lineas.filter((l) => l.refaccion || l.descripcionLibre.trim());
  const elegida = sucursales.find((s) => s.id === sucursalId);

  /**
   * Qué falta, dicho antes de apretar.
   *
   * Un botón apagado sin explicación se lee como «no me deja», y quien lo
   * mira culpa al último campo que tocó. Aquí pasó: faltaba la pieza y
   * parecía que la sucursal no se había podido elegir.
   */
  const faltantes: string[] = [];
  if (!sucursalId) faltantes.push('elegir la sucursal');
  if (completas.length === 0) {
    faltantes.push('elegir una refacción o describir qué se necesita');
  }

  const listo = faltantes.length === 0;

  async function guardar() {
    setGuardando(true);
    setError(null);
    try {
      await api.post('/solicitudes-almacen/anticipada', {
        sucursalId,
        notasParaAlmacen: notas.trim() || undefined,
        partidas: completas.map((l) => ({
          refaccionId: l.refaccion?.id,
          descripcionLibre: l.refaccion ? undefined : l.descripcionLibre.trim(),
          cantidad: Number(l.cantidad) || 1,
        })),
      });
      onHecho();
    } catch (e) {
      setError(e);
      setGuardando(false);
    }
  }

  return (
    <Dialogo
      titulo="Pedir refacciones por anticipado"
      ancho
      onCerrar={onCerrar}
      pie={(
        <>
          <button onClick={onCerrar} disabled={guardando}>Cancelar</button>
          <button className="primario" onClick={guardar} disabled={!listo || guardando}>
            {guardando ? 'Pidiendo…' : 'Pedir a almacén'}
          </button>
        </>
      )}
    >
      <Error error={error} />

      <p className="ayuda">
        Para una sucursal, sin orden todavía. Almacén y compras empiezan a
        trabajarlas de inmediato; se entregan cuando llegue la orden de esa
        visita.
      </p>

      <div className="campo">
        <label htmlFor="suc">Sucursal</label>

        {/* Elegida, se enseña y la lista se retira.
            Dejar la lista abierta con un renglón resaltado no dice si el
            clic tomó: el resaltado también aparece al mover el teclado. */}
        {elegida ? (
          <div className="controles">
            <strong>{elegida.determinante} — {elegida.nombre}</strong>
            <button className="enlace" onClick={() => setSucursalId('')}>
              Cambiar
            </button>
          </div>
        ) : (
          <>
            <input
              id="suc"
              type="search"
              value={busqueda}
              placeholder="Determinante o nombre…"
              onChange={(e) => { setBusqueda(e.target.value); setSucursalId(''); }}
            />
            {sucursales.length > 0 && (
              <Resultados
                de={sucursales}
                clave={(s) => s.id}
                como={(s) => `${s.determinante} — ${s.nombre}`}
                onElegir={(s) => setSucursalId(s.id)}
              />
            )}
            {busqueda.trim().length >= 2 && sucursales.length === 0 && (
              <span className="ayuda">Ninguna sucursal con esa búsqueda.</span>
            )}
          </>
        )}
      </div>

      {lineas.map((linea, i) => (
        <div key={i} className="panel" style={{ marginBottom: 8 }}>
          <BuscadorRefaccion
            id={`anticipada-refaccion-${i}`}
            valor={linea.refaccion}
            onElegir={(r) => cambiar(i, { refaccion: r })}
          />

          {!linea.refaccion && (
            <div className="campo">
              <label htmlFor={`anticipada-libre-${i}`}>
                Si no está en el catálogo, descríbela
              </label>
              <input
                id={`anticipada-libre-${i}`}
                value={linea.descripcionLibre}
                maxLength={250}
                onChange={(e) => cambiar(i, { descripcionLibre: e.target.value })}
              />
            </div>
          )}

          <div className="campo">
            <label htmlFor={`anticipada-cantidad-${i}`}>Cantidad</label>
            <input
              id={`anticipada-cantidad-${i}`}
              type="number"
              min={1}
              value={linea.cantidad}
              onChange={(e) => cambiar(i, { cantidad: e.target.value })}
            />
          </div>

          {lineas.length > 1 && (
            <button
              className="enlace"
              onClick={() => setLineas((antes) => antes.filter((_, n) => n !== i))}
            >
              Quitar esta pieza
            </button>
          )}
        </div>
      ))}

      <button
        onClick={() => setLineas((antes) => [
          ...antes, { refaccion: null, descripcionLibre: '', cantidad: '1' },
        ])}
      >
        Agregar otra pieza
      </button>

      <div className="campo">
        <label htmlFor="notas-anticipada">Notas para almacén</label>
        <textarea
          id="notas-anticipada"
          value={notas}
          maxLength={1000}
          rows={2}
          onChange={(e) => setNotas(e.target.value)}
        />
      </div>

      <Faltantes que={faltantes} />
    </Dialogo>
  );
}

/**
 * La orden llegó: se le agregan las piezas anticipadas.
 *
 * Se ofrecen las órdenes de esa misma sucursal, que es lo que la API va a
 * exigir. Enlazarla a otra tienda mandaría las piezas al lugar equivocado
 * con el papeleo en regla.
 */
function AgregarAUnaOrden({ solicitud, onCerrar, onHecho }: {
  solicitud: SolicitudAnticipada;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [ordenes, setOrdenes] = useState<Orden[] | null>(null);
  const [ordenId, setOrdenId] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!solicitud.sucursal) { setOrdenes([]); return; }
    /**
     * Las dos clases de orden, no sólo las preventivas.
     *
     * Karin: «pueden relacionarse también con una correctiva […] por la
     * urgencia». La anticipada nace en el preventivo y ahí se queda, pero
     * la pieza que llegó puede acabar usándose en una correctiva de la
     * misma sucursal, y hasta ahora el selector no las ofrecía.
     *
     * La API nunca lo prohibió: acepta cualquier orden abierta de la
     * misma sucursal. El límite estaba aquí, en el filtro por tipo.
     */
    api.get<Pagina<Orden>>('/ordenes', {
      sucursalId: solicitud.sucursal.id,
      bandeja: 'abiertas',
      limite: 50,
    })
      .then((r) => setOrdenes(r.data))
      .catch(setError);
  }, [solicitud.sucursal]);

  async function guardar() {
    setGuardando(true);
    setError(null);
    try {
      await api.post(`/solicitudes-almacen/${solicitud.id}/enlazar`, {
        ordenTrabajoId: ordenId,
      });
      onHecho();
    } catch (e) {
      setError(e);
      setGuardando(false);
    }
  }

  return (
    <Dialogo
      titulo={`Agregar ${solicitud.folio} a una orden`}
      onCerrar={onCerrar}
      pie={(
        <>
          <button onClick={onCerrar} disabled={guardando}>Cancelar</button>
          <button className="primario" onClick={guardar} disabled={!ordenId || guardando}>
            {guardando ? 'Agregando…' : 'Agregar'}
          </button>
        </>
      )}
    >
      <Error error={error} />

      <p className="ayuda">
        Órdenes abiertas de{' '}
        {solicitud.sucursal
          ? `${solicitud.sucursal.determinante} — ${solicitud.sucursal.nombre}`
          : 'esa sucursal'}
        , preventivas y correctivas. A partir de que se agregue, almacén ya
        puede surtirlas.
      </p>

      {!ordenes ? <Cargando /> : ordenes.length === 0 ? (
        <div className="vacio">
          Esa sucursal no tiene ninguna orden abierta. Cuando llegue del
          archivo del cliente, vuelve aquí.
        </div>
      ) : (
        <div className="campo">
          <label htmlFor="orden-anticipada">Orden</label>
          <select
            id="orden-anticipada"
            value={ordenId}
            onChange={(e) => setOrdenId(e.target.value)}
          >
            <option value="">Elige la orden…</option>
            {/* Con el tipo y en qué va: una sucursal puede tener varias
                abiertas, y el folio solo no alcanza para escoger bien. */}
            {ordenes.map((o) => (
              <option key={o.id} value={o.id}>
                {folioOT(o.folio)}
                {` · ${o.tipo === 'preventivo' ? 'Preventiva' : 'Correctiva'}`}
                {o.noOsCliente ? ` · OS ${o.noOsCliente}` : ''}
                {` · ${ETIQUETAS_ESTATUS[o.estatus] ?? o.estatus}`}
              </option>
            ))}
          </select>
        </div>
      )}
    </Dialogo>
  );
}

function ProgramaDelMes() {
  const { puede } = useSesion();
  const hoy = new Date();

  /**
   * El mes que se abre, con la URL mandando sobre el de hoy.
   *
   * Karin: «en "preventivo" y desde ahi gestionamos esas». Para que el
   * enlace desde una orden sirva, tiene que aterrizar en el mes de **su**
   * visita: llegar al mes corriente y tener que buscar septiembre a mano
   * convierte un enlace en una pista.
   *
   * Se lee una vez, al montar. Después manda el estado: cambiar de mes
   * en la pantalla no debe pelearse con lo que diga la dirección.
   */
  const [params] = useSearchParams();
  const deLaUrl = (clave: string, porOmision: number): number => {
    const valor = Number(params.get(clave));
    return Number.isInteger(valor) && valor > 0 ? valor : porOmision;
  };

  const [anio, setAnio] = useState(() => deLaUrl('anio', hoy.getFullYear()));
  const [mes, setMes] = useState(() => deLaUrl('mes', hoy.getMonth() + 1));
  const [tecnicoId, setTecnicoId] = useState('');
  const [estatus, setEstatus] = useState('');
  const [busqueda, setBusqueda] = useState('');

  const { regionId, estadoId, selectores } = useZona();

  const [programa, setPrograma] = useState<ProgramaPreventivo | null>(null);
  const [tecnicos, setTecnicos] = useState<OpcionTecnico[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [armando, setArmando] = useState(false);
  const [poniendoAlDia, setPoniendoAlDia] = useState(false);
  const [abierta, setAbierta] = useState<VisitaPreventiva | null>(null);

  /**
   * La visita que pidió la dirección, para abrirla sola.
   *
   * Karin: «sera posible que desde una OT preventiva se llegue a su
   * vista de preventivo?». Llegar al mes correcto ya era la mitad; la
   * otra mitad era encontrar el renglón entre los ochenta del mes y
   * acordarse de cuál sucursal era. Con el id en la dirección, el panel
   * de esa visita se abre al cargar el programa.
   *
   * Se gasta una vez y se olvida: si después se cierra el panel, o se
   * cambia de mes, no se vuelve a abrir solo. Un panel que reaparece
   * cada vez que se recarga la tabla es imposible de cerrar.
   */
  const visitaPedida = params.get('visita');
  const yaSeAbrio = useRef(false);

  const programa_ = puede('preventivo', 'crear');
  const reparte = puede('preventivo', 'editar');

  /**
   * Los técnicos, acotados a la zona que se está mirando.
   *
   * Van por región y no por estado porque el técnico se asigna a una
   * zona, que es como APRISA reparte el trabajo: no hay técnicos «de
   * Yucatán», hay técnicos de península.
   *
   * Si el que estaba elegido no trabaja la zona nueva, el filtro se
   * limpia. Dejarlo puesto deja la tabla vacía pidiendo a alguien que ya
   * no está en la lista, y eso se lee como «no hay nada programado».
   */
  useEffect(() => {
    api.get<OpcionTecnico[]>('/tecnicos/opciones', { regionId: regionId || undefined })
      .then((lista) => {
        setTecnicos(lista);
        setTecnicoId((antes) => (
          antes && antes !== '__sin' && !lista.some((t) => t.id === antes) ? '' : antes
        ));
      })
      .catch(() => undefined);
  }, [regionId]);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<ProgramaPreventivo>('/preventivo', {
      anio,
      mes,
      regionId: regionId || undefined,
      estadoId: estadoId || undefined,
      // «Sin asignar» no es un técnico: es la ausencia de uno, y la API
      // lo pregunta con su propio filtro. Mandarlo como id daría un 400,
      // porque ahí espera un UUID.
      tecnicoId: tecnicoId && tecnicoId !== '__sin' ? tecnicoId : undefined,
      sinAsignar: tecnicoId === '__sin' ? 'si' : undefined,
      estatus: estatus || undefined,
      busqueda: busqueda || undefined,
    })
      .then(setPrograma)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [anio, mes, regionId, estadoId, tecnicoId, estatus, busqueda]);

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, busqueda]);

  /**
   * Y en cuanto llega el programa, se abre la que venía en la dirección.
   *
   * Si no está en la lista no se hace nada y la bandera no se gasta: el
   * mes puede venir filtrado por zona o por técnico, y vale la pena
   * volver a intentarlo cuando se quite el filtro en vez de perder el
   * viaje en silencio.
   */
  useEffect(() => {
    if (!visitaPedida || yaSeAbrio.current || !programa) return;
    const esa = programa.visitas.find((v) => v.id === visitaPedida);
    if (!esa) return;
    yaSeAbrio.current = true;
    setAbierta(esa);
  }, [programa, visitaPedida]);

  async function ponerAlDia() {
    setError(null);
    setPoniendoAlDia(true);
    try {
      await api.post('/preventivo/poner-al-dia', { anio, mes });
      cargar();
    } catch (e) {
      setError(e);
    } finally {
      setPoniendoAlDia(false);
    }
  }

  return (
    <>
      {/*
        El mes no se arma: lo llenan las OT.

        Es como trabaja APRISA —el día primero se cargan las preventivas
        del mes, y si llegan más otro día se agregan—, así que el botón
        dejó de ser el paso principal y pasó a ser lo que de verdad es:
        adelantarse. Programar una sucursal antes de que llegue su orden
        sirve para pedirle refacciones, que tardan más de treinta días.
      */}
      {programa_ && (
        <div className="controles" style={{ justifyContent: 'flex-end' }}>
          <button onClick={() => setArmando(true)}>
            Programar por adelantado
          </button>
        </div>
      )}

      {/*
        Las órdenes que están en Órdenes y no aquí.

        Debería no aparecer nunca: la carga mete cada OT preventiva en el
        programa. Aparece cuando una orden entró con reglas anteriores
        —como las segundas OT de una misma sucursal, que la regla vieja
        descartaba— y esas no se reescriben solas.

        Se ofrece la acción junto al número, no en un menú: quien ve el
        descuadre es quien lo puede arreglar, y en ese momento.
      */}
      {programa && programa.avance.sinVisita > 0 && programa_ && (
        <div className="aviso">
          <strong>
            {programa.avance.sinVisita} orden(es) preventiva(s) de{' '}
            {programa.nombre} no están en el programa.
          </strong>
          <p style={{ margin: '6px 0' }}>
            Están en Órdenes pero no aquí, casi siempre porque entraron
            cuando el programa todavía no admitía dos OT para la misma
            sucursal. Se pueden meter ahora con las reglas de hoy.
          </p>
          <button disabled={poniendoAlDia} onClick={() => { void ponerAlDia(); }}>
            {poniendoAlDia ? 'Poniendo al día…' : 'Poner el mes al día'}
          </button>
        </div>
      )}

      {programa && (
        <div className="rejilla-catalogos">
          {/* Primero lo que falta: el programa existe para que nadie se
              quede sin visitar, no para presumir lo hecho. */}
          <Tarjeta
            titulo="Pendientes"
            valor={programa.avance.pendientes}
            alerta={programa.avance.pendientes > 0}
          />
          <Tarjeta titulo="Realizadas" valor={programa.avance.realizadas} />
          <Tarjeta
            titulo="No se hicieron"
            valor={programa.avance.noRealizadas}
            alerta={programa.avance.noRealizadas > 0}
          />
          {/*
            Las que tocaban y de las que no llegó OT.

            Es el número que ninguna de las dos fuentes da sola: el mes lo
            llena la carga, y el calendario sirve ya sólo para saber qué
            no llegó. Una sucursal que sale aquí dos meses seguidos es una
            preventiva que no se está haciendo, o un ciclo trimestral mal
            capturado.
          */}
          <Tarjeta
            titulo="Sin OT, y tocaban"
            valor={programa.avance.faltantes}
            alerta={programa.avance.faltantes > 0}
          />
          {/*
            La tienda con dos OT del cliente.

            Pasa: Walmart manda dos órdenes para la misma sucursal y APRISA
            registra lo que llegó. Se cuentan aparte porque son las únicas
            del mes que necesitan que alguien decida algo — rechazar la que
            sobra— y sin la tarjeta habría que ir a buscarlas fila por fila.
          */}
          <Tarjeta
            titulo="Con más de una OT"
            valor={programa.avance.conVariasOrdenes}
            alerta={programa.avance.conVariasOrdenes > 0}
          />
          <Tarjeta titulo="En el programa" valor={programa.avance.total} />
        </div>
      )}

      <div className="panel">
        <div className="controles">
          <select value={mes} onChange={(e) => setMes(Number(e.target.value))}>
            {MESES.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
          <select value={anio} onChange={(e) => setAnio(Number(e.target.value))}>
            {[hoy.getFullYear() - 1, hoy.getFullYear(), hoy.getFullYear() + 1].map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          {selectores}
          <select value={tecnicoId} onChange={(e) => setTecnicoId(e.target.value)}>
            <option value="">
              {regionId ? 'Todos los técnicos de la zona' : 'Todos los técnicos'}
            </option>
            {/* Lo que falta por repartir. Es la primera pregunta de quien
                arma el mes: ¿ya tienen dueño todas? */}
            <option value="__sin">Sin asignar</option>
            <OpcionesDeTecnico tecnicos={tecnicos} />
          </select>
          <select value={estatus} onChange={(e) => setEstatus(e.target.value)}>
            <option value="">Todo el mes</option>
            <option value="programada">Sólo lo pendiente</option>
            <option value="realizada">Sólo lo realizado</option>
            <option value="no_realizada">Sólo lo que no se hizo</option>
          </select>
          {/* Karin: «en preventivo déjame buscar por OT también». El folio
              llega en un correo o lo dice alguien por teléfono, y la
              tienda es justo lo que se quiere averiguar: buscar sólo por
              sucursal obliga a saberse de memoria a cuál pertenece. */}
          <input
            type="search"
            placeholder="Sucursal, determinante, folio de OT u OS…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>

        <Error error={error} />

        {cargando && !programa ? <Cargando /> : programa && (
          <>
            {programa.visitas.length === 0 ? (
              <div className="vacio">
                {/* Con un filtro puesto, «no hay programa» es falso: lo
                    que no hay es lo que se está pidiendo. Ofrecer ahí
                    «Armar el mes» invita a armar dos veces el mismo. */}
                {regionId || estadoId || tecnicoId || estatus || busqueda
                  ? `Nada en ${programa.nombre} con esos filtros.`
                  : `Todavía no hay nada en ${programa.nombre}. El mes se llena`
                    + ' solo conforme se cargan las OT preventivas; si hace falta'
                    + ' adelantarse para pedir refacciones, se puede programar'
                    + ' a mano.'}
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Sucursal</th>
                    <th>Zona</th>
                    <th>Estado</th>
                    <th>Iguala</th>
                    <th>Técnico</th>
                    <th>Estatus</th>
                    <th>Orden</th>
                    <th>OS</th>
                  </tr>
                </thead>
                <tbody>
                  {programa.visitas.map((v) => (
                    <tr key={v.id}>
                      <td>
                        <button className="enlace" onClick={() => setAbierta(v)}>
                          {v.sucursal
                            ? `${v.sucursal.determinante} — ${v.sucursal.nombre}`
                            : '—'}
                        </button>
                        {/*
                          La que se programó a mano se distingue de la que
                          llegó con su OT. Sin la marca, una sucursal que
                          alguien adelantó para pedir refacciones se ve
                          igual que una que Walmart mandó, y la diferencia
                          entre las dos es justo lo que hay que revisar.
                        */}
                        {v.origen === 'programa' && !v.ordenTrabajo && (
                          <div className="ayuda">Programada por adelantado</div>
                        )}
                        {/*
                          Dos OT del cliente para la misma tienda.

                          Se dice en la fila, no sólo en la tarjeta: quien
                          ve la sucursal repetida en la lista tiene derecho
                          a saber ahí mismo que no es un error del sistema
                          —es lo que mandó Walmart— y qué se hace con ella.
                        */}
                        {v.otraOrdenEnElMes && (
                          <div className="etiqueta no" style={{ marginTop: 4 }}>
                            El cliente mandó más de una OT · rechaza la que sobre
                          </div>
                        )}
                      </td>
                      <td>{v.sucursal?.region?.nombre ?? '—'}</td>
                      <td>{v.sucursal?.estado?.nombre ?? '—'}</td>
                      <td>{v.sucursal?.tipo_iguala?.nombre ?? '—'}</td>
                      <td>
                        {v.tecnico?.nombre_completo
                          ?? <span className="etiqueta no">Sin asignar</span>}
                      </td>
                      <td>
                        {/* La etiqueta la arma la API: junta el estado de
                            la visita con el de su orden. «Realizada» sobre
                            una que espera su refacción era cierto y
                            confundía. */}
                        <span className={`etiqueta ${v.enQueVa.clase}`}>
                          {v.enQueVa.texto}
                        </span>
                      </td>
                      <td className="mono">
                        {v.ordenTrabajo ? folioOT(v.ordenTrabajo.folio) : '—'}
                        {v.enlaceAutomatico && (
                          <div className="ayuda">La enlazó la carga</div>
                        )}
                      </td>
                      {/*
                        La OS del técnico que se adelantó. Mientras no
                        llegue la OT es lo único que sostiene el acuse, y
                        por eso está en la tabla y no escondida en la
                        ficha: es la columna que dice qué está esperando
                        su orden.
                      */}
                      <td className="mono">
                        {v.noOs ?? '—'}
                        {v.noOs && !v.ordenTrabajo && (
                          <div className="ayuda">Esperando su OT</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>

      {armando && (
        <ArmarMes
          anio={anio}
          mes={mes}
          regionId={regionId}
          estadoId={estadoId}
          onCerrar={() => setArmando(false)}
          onHecho={() => { setArmando(false); cargar(); }}
        />
      )}

      {abierta && (
        <FichaVisita
          visita={abierta}
          reparte={reparte}
          onCerrar={() => setAbierta(null)}
          onHecho={() => { setAbierta(null); cargar(); }}
        />
      )}
    </>
  );
}

/**
 * Los cuatro meses en que se visita una trimestral de ese ciclo.
 *
 * Sólo es la etiqueta. La regla vive en la API —`mesesDelCiclo`, probada
 * ahí— y aquí sólo se escribe cómo se lee.
 */
function mesesDelCiclo(ciclo: number): string {
  return [0, 1, 2, 3]
    .map((n) => MESES[(ciclo - 1 + n * 3) % 12])
    .map((m) => m.slice(0, 3))
    .map((m) => m[0].toUpperCase() + m.slice(1))
    .join(' · ');
}

/**
 * El año de las trimestrales: a cuál de los tres ciclos va cada una.
 *
 * Por omisión todas cuentan desde enero, que es el valor de la columna
 * al crearse. Con eso, las trescientas y pico caen juntas en enero,
 * abril, julio y octubre: cuatro meses inabordables y ocho vacíos. Aquí
 * se reparten.
 *
 * El ciclo se elige por los meses en que va a tocar y no por un «mes
 * ancla», porque de doce meses sólo salen tres trimestres distintos:
 * elegir enero o abril es elegir lo mismo, y ofrecer las doce opciones
 * hace creer que se decidió algo que no se decidió.
 */
function Trimestrales({ reparte }: { reparte: boolean }) {
  const { regionId, estadoId, selectores } = useZona();
  const [busqueda, setBusqueda] = useState('');

  const [sucursales, setSucursales] = useState<SucursalTrimestral[]>([]);
  const [cambios, setCambios] = useState<Record<string, number>>({});
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<TrimestralesRespuesta>('/preventivo/trimestrales', {
      regionId: regionId || undefined,
      estadoId: estadoId || undefined,
      busqueda: busqueda || undefined,
    })
      .then((r) => {
        setSucursales(r.sucursales);
        // Lo pendiente se descarta al recargar: dejarlo vivo sobre una
        // lista distinta guardaría cambios que ya no se ven.
        setCambios({});
      })
      .catch(setError)
      .finally(() => setCargando(false));
  }, [regionId, estadoId, busqueda]);

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, busqueda]);

  const cicloDe = (s: SucursalTrimestral) => cambios[s.sucursalId] ?? s.ciclo;
  const pendientes = sucursales.filter((s) => cicloDe(s) !== s.ciclo);

  function poner(sucursalId: string, ciclo: number) {
    setCambios((antes) => ({ ...antes, [sucursalId]: ciclo }));
  }

  /** El reparto que propone la API, aplicado a lo que está en pantalla. */
  function repartir() {
    setCambios(Object.fromEntries(
      sucursales.map((s) => [s.sucursalId, s.cicloSugerido]),
    ));
  }

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post('/preventivo/anclas', {
        // El ciclo es el mes ancla: 1, 2 y 3 son anclas válidas y son
        // los tres trimestres distintos que existen.
        sucursales: pendientes.map((s) => ({
          sucursalId: s.sucursalId,
          mesAncla: cicloDe(s),
        })),
      });
      cargar();
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <>
      <div className="rejilla-catalogos">
        {[1, 2, 3].map((ciclo) => {
          const cuantas = sucursales.filter((s) => cicloDe(s) === ciclo).length;

          return (
            <Tarjeta
              key={ciclo}
              titulo={mesesDelCiclo(ciclo)}
              valor={cuantas}
              // Un ciclo vacío es el síntoma de que nadie ha repartido:
              // ocho meses del año sin una sola preventiva trimestral.
              alerta={sucursales.length > 0 && cuantas === 0}
            />
          );
        })}
      </div>

      <div className="panel">
        <div className="controles">
          {selectores}
          <input
            type="search"
            placeholder="Sucursal o determinante…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
          {reparte && sucursales.length > 0 && (
            <button onClick={repartir} disabled={trabajando}>
              Repartir en tres
            </button>
          )}
          {reparte && pendientes.length > 0 && (
            <>
              <button
                className="primario"
                disabled={trabajando}
                onClick={() => void guardar()}
              >
                {trabajando ? 'Guardando…' : `Guardar ${pendientes.length} cambio(s)`}
              </button>
              <button disabled={trabajando} onClick={() => setCambios({})}>
                Deshacer
              </button>
            </>
          )}
        </div>

        <p className="ayuda">
          Cada sucursal trimestral se visita cuatro veces al año. Lo que
          se elige aquí es en cuáles cuatro. «Repartir en tres» propone un
          acomodo parejo por zona; revísalo y muévelo antes de guardar,
          porque las que el cliente pidió en un mes concreto no las sabe
          el sistema.
        </p>

        <Error error={error} />

        {cargando && sucursales.length === 0 ? <Cargando /> : sucursales.length === 0 ? (
          <div className="vacio">
            No hay sucursales de iguala trimestral con esos filtros.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Sucursal</th>
                <th>Zona</th>
                <th>Estado</th>
                <th>Iguala</th>
                <th>Se visita en</th>
              </tr>
            </thead>
            <tbody>
              {sucursales.map((s) => {
                const ciclo = cicloDe(s);

                return (
                  <tr key={s.sucursalId}>
                    <td>{s.determinante} — {s.nombre}</td>
                    <td>{s.region?.nombre ?? '—'}</td>
                    <td>{s.estado?.nombre ?? '—'}</td>
                    <td>{s.iguala?.nombre ?? '—'}</td>
                    <td>
                      {reparte ? (
                        <select
                          value={ciclo}
                          onChange={(e) => poner(s.sucursalId, Number(e.target.value))}
                        >
                          {[1, 2, 3].map((c) => (
                            <option key={c} value={c}>{mesesDelCiclo(c)}</option>
                          ))}
                        </select>
                      ) : mesesDelCiclo(ciclo)}
                      {ciclo !== s.ciclo && (
                        <span className="etiqueta" style={{ marginLeft: 8 }}>
                          sin guardar
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

/**
 * Armar el mes desde la propuesta.
 *
 * El sistema propone según la iguala de cada sucursal —full mensual,
 * trimestral cada tres meses desde su ancla— y quien programa decide qué
 * entra. Propone, no impone: hay meses en que una zona no se puede
 * atender y eso lo sabe supervisión, no la tabla.
 *
 * Hereda la zona que se esté mirando en la pantalla: quien está armando
 * la península no tiene por qué revisar una lista nacional para quitar
 * lo que no es suyo.
 */
function ArmarMes({ anio, mes, regionId, estadoId, onCerrar, onHecho }: {
  anio: number; mes: number; regionId: string; estadoId: string;
  onCerrar: () => void; onHecho: () => void;
}) {
  const [propuesta, setPropuesta] = useState<PropuestaPreventiva | null>(null);
  const [elegidas, setElegidas] = useState<Set<string>>(new Set());
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  useEffect(() => {
    api.get<PropuestaPreventiva>('/preventivo/propuesta', {
      anio,
      mes,
      regionId: regionId || undefined,
      estadoId: estadoId || undefined,
    })
      .then((p) => {
        setPropuesta(p);
        // Todas marcadas de entrada: lo normal es aceptar la propuesta y
        // quitar las excepciones, no armar el mes de cero.
        setElegidas(new Set(p.sucursales.map((s) => s.sucursalId)));
      })
      .catch(setError);
  }, [anio, mes, regionId, estadoId]);

  function alternar(id: string) {
    setElegidas((antes) => {
      const ahora = new Set(antes);
      if (ahora.has(id)) ahora.delete(id);
      else ahora.add(id);
      return ahora;
    });
  }

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post('/preventivo', { anio, mes, sucursales: [...elegidas] });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={`Armar el programa de ${propuesta?.nombre ?? ''}`}
      ancho
      onCerrar={onCerrar}
      pie={
        <button
          className="primario"
          disabled={elegidas.size === 0 || trabajando}
          onClick={() => void guardar()}
        >
          {trabajando ? 'Armando…' : `Programar ${elegidas.size} sucursal(es)`}
        </button>
      }
    >
      <Error error={error} />

      {!propuesta ? <Cargando /> : propuesta.sucursales.length === 0 ? (
        <div className="vacio">
          No hay sucursales que tocar este mes que no estén ya programadas.
        </div>
      ) : (
        <>
          <p className="ayuda">
            Salen de la iguala de cada sucursal: la full toca todos los
            meses y la trimestral cada tres, contados desde su mes ancla.
            Quita las que no vayan; agregar una que no aparece se hace
            cambiando su ancla o su iguala.
            {(regionId || estadoId) && ' Sólo se están viendo las de la zona'
              + ' que tienes filtrada en la pantalla.'}
          </p>

          <table>
            <thead>
              <tr>
                <th />
                <th>Sucursal</th>
                <th>Zona</th>
                <th>Estado</th>
                <th>Iguala</th>
                <th>Cada cuánto</th>
              </tr>
            </thead>
            <tbody>
              {propuesta.sucursales.map((s) => (
                <tr key={s.sucursalId}>
                  <td>
                    <input
                      type="checkbox"
                      checked={elegidas.has(s.sucursalId)}
                      onChange={() => alternar(s.sucursalId)}
                    />
                  </td>
                  <td>{s.determinante} — {s.nombre}</td>
                  <td>{s.region?.nombre ?? '—'}</td>
                  <td>{s.estado?.nombre ?? '—'}</td>
                  <td>{s.iguala?.nombre ?? '—'}</td>
                  <td>
                    {s.periodicidad === 'mensual'
                      ? 'Mensual'
                      : `Trimestral · desde ${MESES[s.mesAncla - 1]}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Dialogo>
  );
}

/**
 * Cerrar la orden de una preventiva, desde su visita.
 *
 * Karin: «tengo que hacer un flujo para poder cerrar la orden desde el
 * modulo de ordenes, como podemos eficientar esa flujo?»
 *
 * Eran tres pasos en Órdenes: asignar técnico, iniciar trabajo y cerrar.
 * Los dos primeros no son decisiones en una preventiva —el técnico ya lo
 * sabe la visita, e «iniciar» declara que empezó algo que ya terminó— y
 * los camina la API, cada uno por el motor de estados. Aquí queda el
 * tercero, que sí lo es: cerrar manda la orden a facturación.
 *
 * El texto llega redactado con el día, la OS, los servicios y los
 * equipos. Se puede corregir: es lo que va a sostener la factura si el
 * cliente la discute, y quien cierra es quien responde por él.
 */
function CerrarLaPreventiva({
  ordenId, folio, propuesto, noOs: noOsVisita, onCerrar, onHecho,
}: {
  ordenId: string;
  folio: string;
  propuesto: { resultado: string; descripcionTrabajo: string };
  noOs: string | null;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [descripcion, setDescripcion] = useState(propuesto.descripcionTrabajo);
  const [noOs, setNoOs] = useState(noOsVisita ?? '');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);
  /**
   * La OS firmada, el documento.
   *
   * Karin: «si llego a cerrar una orden desde preventivo, me falta el
   * modulo de Orden de servicio firmada […] si tengo la opcion de
   * cerrarla desde la OT o desde preventivo me debe pedir los mismos
   * campos, o no estoy en lo correcto?».
   *
   * Lo estaba. No los mismos campos —el resultado y los servicios ya los
   * dijo la visita, y volver a preguntarlos sería la captura doble que
   * quitamos— pero sí el mismo resultado: una orden cerrada por los dos
   * lados tiene que quedar con lo mismo guardado. El número de OS ya se
   * pedía; el papel no se pedía por ningún lado.
   *
   * Y es el camino donde más falta: una preventiva **se factura
   * siempre**, así que es justo la que necesita el documento que sostiene
   * el cobro si el cliente lo discute.
   */
  const [documento, setDocumento] = useState<{ id: string; nombre: string } | null>(null);
  const [subiendo, setSubiendo] = useState(false);

  const corto = descripcion.trim().length < 10;

  /**
   * Se sube antes de cerrar y no con el cierre.
   *
   * Es el mismo camino que usa Órdenes: la evidencia es su propio
   * recurso, así que si el cierre falla después, el documento ya quedó
   * guardado y no hay que volver a buscarlo. Un archivo que se pierde
   * porque la transición se negó es un archivo que nadie vuelve a subir.
   */
  async function subirOrdenServicio(archivo: File) {
    setError(null);
    setSubiendo(true);
    try {
      const cuerpo = new FormData();
      cuerpo.append('archivo', await aligerar(archivo));

      const respuesta = await fetchAutenticado(
        `/api/v1/ordenes/${ordenId}/evidencias?tipo=orden_servicio`,
        { method: 'POST', body: cuerpo },
      );

      if (!respuesta.ok) {
        const texto = await respuesta.text();
        const cuerpoError = texto ? (JSON.parse(texto) as Record<string, unknown>) : {};
        throw new ErrorApi(
          respuesta.status,
          String(cuerpoError.codigo_negocio ?? 'error'),
          String(cuerpoError.detail ?? 'No se pudo subir la orden de servicio.'),
        );
      }

      const guardada = (await respuesta.json()) as { id: string };
      setDocumento({ id: guardada.id, nombre: archivo.name });
    } catch (e) {
      setError(e);
    } finally {
      setSubiendo(false);
    }
  }

  async function cerrar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post(`/ordenes/${ordenId}/cerrar-preventiva`, {
        resultado: propuesto.resultado,
        descripcionTrabajo: descripcion.trim(),
        noOsCliente: noOs.trim() || undefined,
      });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={`Cerrar la orden ${folioOT(folio)}`}
      onCerrar={onCerrar}
      pie={
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button
            className="primario" onClick={() => { void cerrar(); }}
            disabled={trabajando || corto || subiendo}
          >
            {trabajando ? 'Cerrando…' : 'Cerrar y mandar a facturación'}
          </button>
        </>
      }
    >
      <Error error={error} />

      <p className="sub">
        La orden queda cerrada y pasa a facturación. Si le falta el técnico
        o no se había iniciado, se resuelve solo: en una preventiva esos dos
        pasos no son decisiones, el trabajo ya se hizo y está firmado.
      </p>

      <div className="campo">
        <label htmlFor="cierreTrabajo">Descripción del trabajo</label>
        <textarea
          id="cierreTrabajo" rows={5} value={descripcion} maxLength={2000}
          onChange={(e) => setDescripcion(e.target.value)}
        />
        <span className="ayuda">
          {/* Se puede corregir a propósito: es el texto que sostiene la
              factura, y quien cierra responde por él. */}
          Viene redactada con el día, la OS, los servicios y los equipos.
          Corrige lo que haga falta — es lo que se lee si el cliente
          discute la factura.
        </span>
      </div>

      <div className="campo">
        <label htmlFor="cierreOs">Número de OS firmada</label>
        <input
          id="cierreOs" value={noOs} maxLength={50}
          onChange={(e) => setNoOs(e.target.value)}
          placeholder="El papel que firmó la sucursal"
        />
        <span className="ayuda">
          Se trae el de la visita. Si el papel llegó con otro folio, cámbialo.
        </span>
      </div>

      {/*
        El documento, igual que al cerrar desde la orden.

        Mismos formatos y mismo tipo de evidencia —`orden_servicio`— para
        que la orden quede idéntica se cierre por donde se cierre. Que
        dependiera de la pantalla por la que entraste es justo lo que
        hacía que unas tuvieran el papel y otras no.
      */}
      <div className="campo">
        <label htmlFor="cierreOsFirmada">Orden de servicio firmada</label>
        {documento ? (
          <span className="etiqueta si">Subida: {documento.nombre}</span>
        ) : (
          <input
            id="cierreOsFirmada"
            type="file"
            accept={'image/jpeg,image/png,image/webp,application/pdf,'
              + 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'}
            disabled={subiendo || trabajando}
            onChange={(e) => {
              const archivo = e.target.files?.[0];
              if (archivo) void subirOrdenServicio(archivo);
            }}
          />
        )}
        <span className="ayuda">
          Foto, escaneo o documento (PDF, DOCX), hasta 10 MB. Queda en la
          evidencia de la orden. Una preventiva se factura siempre, así que
          éste es el papel que la sostiene si el cliente la discute.
        </span>
      </div>

      {corto && (
        <span className="ayuda">
          La descripción tiene que decir algo: al menos 10 caracteres.
        </span>
      )}
    </Dialogo>
  );
}

/**
 * La visita: a quién le toca, y cerrarla contra su orden.
 *
 * Los técnicos se piden aquí y no se heredan de la pantalla: la lista
 * que toca es la de la zona de *esta* sucursal, se esté filtrando o no
 * arriba. Abrir una visita de la península y que se ofrezcan los 32
 * técnicos del país es cómo se asigna a alguien que está a mil
 * kilómetros.
 */
function FichaVisita({ visita: delListado, reparte, onCerrar, onHecho }: {
  visita: VisitaPreventiva;
  reparte: boolean;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  /**
   * La visita, pedida de nuevo al abrir la ficha.
   *
   * El renglón del listado no trae `pendientesDeCierre` —calcularlo para
   * las visitas de un mes entero serían cientos de consultas— y sin eso
   * la ficha ofrecía cerrar una orden que está esperando su refacción.
   * Mientras llega se usa el del listado, que tiene todo lo demás: así la
   * ficha abre llena y no en blanco.
   */
  const [completa, setCompleta] = useState<VisitaPreventiva | null>(null);
  const visita = completa ?? delListado;

  useEffect(() => {
    api.get<VisitaPreventiva>(`/preventivo/${delListado.id}`)
      .then(setCompleta)
      .catch(() => undefined);
  }, [delListado.id]);

  const [tecnicoId, setTecnicoId] = useState(delListado.tecnico?.id ?? '');
  const [tecnicos, setTecnicos] = useState<OpcionTecnico[]>([]);
  /**
   * El id de la orden elegida, no su folio: es lo que la API guarda.
   *
   * Arranca con la que la visita ya tiene, si la tiene. Empezaba siempre
   * vacío y por eso la pantalla se contradecía: arriba decía «OT 778120,
   * la enlazó la carga» y abajo el desplegable seguía en «Todavía no
   * llega su OT», pidiendo que se eligiera la que ya estaba puesta.
   *
   * Karin: «dice que ya enlazó la OT pero me dice que confirme cuál es».
   * Son dos estados distintos —enlazada y acusada— y la pantalla los
   * enseñaba como uno solo.
   */
  const [ordenId, setOrdenId] = useState(visita.ordenTrabajo?.id ?? '');
  /** La OS firmada, para acusar antes de que llegue la OT. */
  const [noOs, setNoOs] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [noSeHizo, setNoSeHizo] = useState(false);
  const [rechazando, setRechazando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [pactados, setPactados] = useState<ServiciosPactados | null>(null);
  /**
   * Los servicios palomeados.
   *
   * Vacío al acusar por primera vez, y es deliberado. Karin, después de
   * cerrar una con dos servicios sin querer: «no me pongas los servicios
   * premarcados». Cada renglón palomeado es un renglón facturado.
   *
   * Pero al **corregir** se arranca con lo que ya está capturado, y por
   * el mismo motivo al revés: el acuse se rehace desde cero —la API borra
   * y reescribe— así que empezar vacío borraría en silencio los dos
   * servicios que sí se habían registrado, sólo porque alguien entró a
   * agregar el tercero.
   */
  const [hechos, setHechos] = useState<Set<string>>(
    () => new Set(visita.acuse?.esCorreccion ? (visita.serviciosElegidos ?? []) : []),
  );
  /**
   * Los equipos de cada servicio, tal como se teclean: servicio → texto,
   * un equipo por renglón. Se parten al mandar.
   */
  const [equipos, setEquipos] = useState<Record<string, string>>({});
  const [candidatas, setCandidatas] = useState<OrdenCandidata[] | null>(null);
  const [viendoOrden, setViendoOrden] = useState(false);
  const [cerrando, setCerrando] = useState(false);

  /**
   * Si el formulario del acuse se enseña.
   *
   * Era `estatus === 'programada'`, y eso dejaba sin salida a la visita
   * acusada sin servicios. Karin: «desde preventivo no me deja ponerlo,
   * yo creo que por que le dio cerrar». La orden pedía los servicios y
   * esta pantalla era el único sitio donde se capturan.
   *
   * Ahora lo dice la API —`acuse.sePuede`— que es quien tiene la regla:
   * se corrige mientras la orden siga abierta, y se para cuando ya cerró
   * porque entonces los servicios son lo que se factura.
   */
  const abierta = visita.acuse?.sePuede ?? visita.estatus === 'programada';
  const zonaId = visita.sucursal?.region?.id;
  const sucursalId = visita.sucursal?.id;

  useEffect(() => {
    api.get<OpcionTecnico[]>('/tecnicos/opciones', { regionId: zonaId || undefined })
      .then(setTecnicos)
      .catch(() => undefined);
  }, [zonaId]);

  /**
   * Los servicios pactados de esta sucursal, sin palomear.
   *
   * Venían palomeados de entrada, suponiendo que la visita cumple lo
   * contratado y que se despaloma lo que no se hizo. Karin, después de
   * cerrar una con dos servicios sin querer: «no me pongas los servicios
   * premarcados […] déjalos inactivos para que soporte sólo seleccione
   * el que va».
   *
   * Tenía razón, y el motivo pesa más que la comodidad: cada renglón
   * palomeado es un renglón facturado. Palomear por omisión convierte un
   * clic que nadie dio en dinero cobrado, y eso no se ve hasta que el
   * cliente objeta la factura.
   *
   * Lo que se pierde es que ahora hay que marcar uno por uno. Lo que se
   * gana es que nada se cobra sin que alguien lo haya dicho.
   */
  useEffect(() => {
    if (!sucursalId) return;
    api.get<ServiciosPactados>('/servicios-preventivos/pactados', { sucursalId })
      .then(setPactados)
      .catch(() => undefined);
  }, [sucursalId]);

  /**
   * Las órdenes que pueden cumplir esta visita.
   *
   * Sólo hace falta mientras la visita sigue abierta: una ya cerrada
   * enseña la suya y no se va a elegir otra.
   */
  useEffect(() => {
    if (!abierta || !reparte) return;
    api.get<OrdenCandidata[]>(`/preventivo/${visita.id}/ordenes-candidatas`)
      .then(setCandidatas)
      .catch(() => setCandidatas([]));
  }, [visita.id, abierta, reparte]);

  const pactadosDeLaSucursal = pactados?.servicios.filter((s) => s.pactado) ?? [];

  /**
   * El que ya estaba asignado, aunque no sea de esta zona.
   *
   * Pasa cuando alguien cambia de región después de que se repartió el
   * mes. Sin esto el desplegable se abriría en blanco y guardar sin
   * tocarlo lo desasignaría en silencio.
   */
  const asignado = visita.tecnico;
  const fueraDeZona = asignado && !tecnicos.some((t) => t.id === asignado.id)
    ? asignado
    : null;

  async function hacer(accion: () => Promise<unknown>) {
    setError(null);
    setTrabajando(true);
    try {
      await accion();
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={visita.sucursal
        ? `${visita.sucursal.determinante} — ${visita.sucursal.nombre}`
        : 'Visita programada'}
      onCerrar={onCerrar}
    >
      <Error error={error} />

      <div className="rejilla-datos">
        <div>
          <div className="conteo">Estatus</div>
          <span className={`etiqueta ${visita.enQueVa.clase}`}>
            {visita.enQueVa.texto}
          </span>
        </div>
        <div>
          <div className="conteo">Zona</div>
          <div>{visita.sucursal?.region?.nombre ?? '—'}</div>
        </div>
        <div>
          <div className="conteo">Estado</div>
          <div>{visita.sucursal?.estado?.nombre ?? '—'}</div>
        </div>
        {visita.ordenTrabajo && (
          <div>
            <div className="conteo">Orden que la cumplió</div>
            {/* Se abre desde aquí. Acusar la visita no cierra la orden
                —el cierre pide resultado y descripción del trabajo— y
                sin este enlace había que ir a buscarla a mano a Órdenes
                para terminarla. */}
            <button className="enlace mono" onClick={() => setViendoOrden(true)}>
              {folioOT(visita.ordenTrabajo.folio)}
            </button>
            {visita.ordenTrabajo.estatus === 'cierre_admin' && (
              <div className="ayuda">Ya cerrada. Pasó a facturación.</div>
            )}
            {visita.enlaceAutomatico && (
              <div className="ayuda">La enlazó la carga, por sucursal y mes.</div>
            )}
          </div>
        )}
        {visita.noOs && (
          <div>
            <div className="conteo">OS firmada</div>
            <div className="mono">{visita.noOs}</div>
          </div>
        )}
        {visita.realizadaEn && (
          <div>
            <div className="conteo">Realizada</div>
            <div>{fechaCorta(visita.realizadaEn)}</div>
          </div>
        )}
      </div>

      {visita.motivo && (
        <div className="aviso" style={{ marginTop: 12 }}>{visita.motivo}</div>
      )}

      {/*
        La visita acusada que todavía espera su orden.

        Se dice en voz alta porque es un estado nuevo y transitorio: el
        trabajo está hecho y firmado, pero no hay nada que facturar hasta
        que llegue la OT. Sin este aviso parece una visita cerrada normal
        y nadie entendería por qué no tiene servicios.
      */}
      {visita.estatus === 'realizada' && !visita.ordenTrabajo && (
        <div className="aviso" style={{ marginTop: 12 }}>
          El técnico ya fue y hay OS firmada, pero la OT no ha llegado.
          Cuando se cargue, se enlaza sola y ahí se registran los
          servicios con el precio del día de la visita.

          {/*
            Lo que dijo el técnico, ya guardado.

            Sin precio: el precio se congela contra la orden y todavía no
            hay ninguna. Se enseña igual porque es la prueba de que el
            dato no se perdió esperando la OT, que es justo lo que pasaba
            antes.
          */}
          {visita.serviciosCapturados && (
            visita.serviciosAcusados.length > 0 ? (
              <div style={{ marginTop: 8 }}>
                <div className="conteo">Lo que se hizo</div>
                <ul style={{ margin: '4px 0 0 18px' }}>
                  {visita.serviciosAcusados.map((s) => (
                    <li key={s.id}>
                      {s.codigo} · {s.nombre}
                      <ListaDeEquipos equipos={s.equipos} />
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="ayuda" style={{ marginTop: 8 }}>
                Se registró que no se hizo ninguno de los servicios
                pactados, así que la orden no llevará renglones.
              </div>
            )
          )}
        </div>
      )}

      {/*
        Lo que sigue, en un solo botón.

        Es el paso que faltaba: la visita ya está acusada y la orden sigue
        abierta esperando tres clics en otra pantalla. Va destacado y
        arriba de todo lo demás porque es lo único que hay que hacer aquí;
        el resto de esta ficha es consulta.
      */}
      {/*
        Lo que impide cerrar, dicho aquí y no sólo en Órdenes.

        Karin: «si la ves en ordenes la tienes en espera de ser surtida
        pero si la ves en preventivo te dice que la puedes cerrar». Son
        los mismos impedimentos y del mismo módulo puro; lo que faltaba
        era traerlos a esta pantalla.
      */}
      {visita.pendientesDeCierre.length > 0 && (
        <div className="aviso" style={{ marginTop: 12 }}>
          <strong>La orden no se puede cerrar todavía.</strong>
          <ul>
            {visita.pendientesDeCierre.map((p) => <li key={p}>{p}</li>)}
          </ul>
          <div className="ayuda" style={{ marginTop: 6 }}>
            La visita ya está acusada: lo que falta no es del programa
            preventivo. En cuanto se resuelva, aquí aparece el botón de
            cerrar.
          </div>
        </div>
      )}

      {visita.cierrePropuesto && (
        <div className="seccion acento" style={{ marginTop: 12 }}>
          <div className="controles">
            <button className="primario" onClick={() => setCerrando(true)}>
              Cerrar la orden
            </button>
            <span className="ayuda">
              Deja lista la {folioOT(visita.ordenTrabajo?.folio ?? '')} para
              facturar. El texto viene redactado con lo que se hizo; se
              revisa y se confirma.
            </span>
          </div>
        </div>
      )}

      {/*
        Deshacer el emparejamiento.

        Sólo tiene sentido en la que se acusó con su OS: ahí la visita
        conserva el documento que la sostiene y puede volver a esperar. En
        la que se cerró eligiendo la orden a mano, quitársela dejaría una
        visita dada por hecha sin nada detrás, y la API lo rechaza.
      */}
      {reparte && visita.ordenTrabajo && visita.noOs && (
        <div className="controles" style={{ marginTop: 12 }}>
          <button
            disabled={trabajando}
            onClick={() => { void hacer(
              () => api.post(`/preventivo/${visita.id}/desenlazar`, {}),
            ); }}
          >
            No es esta orden
          </button>
          <span className="ayuda">
            Le quita la orden y la visita vuelve a esperar la suya. El
            acuse y la OS se conservan.
          </span>
        </div>
      )}

      {/* Lo que quedó facturable. Se enseña con el precio de ese día, que
          es el que se congeló: si mañana sube el servicio, esta visita
          sigue valiendo lo que valía. */}
      {visita.servicios.length > 0 && (
        <>
          <h3 style={{ marginTop: 16 }}>Servicios registrados</h3>
          <table>
            <tbody>
              {visita.servicios.map((s) => (
                <tr key={s.id}>
                  <td className="mono">{s.codigo}</td>
                  <td>
                    {s.nombre}
                    <ListaDeEquipos equipos={s.equipos} />
                  </td>
                  <td className="numero">
                    {s.precio !== null
                      ? s.precio.toLocaleString('es-MX', {
                        style: 'currency', currency: 'MXN',
                      })
                      : <span className="etiqueta no">sin precio</span>}
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={2}><strong>Total</strong></td>
                <td className="numero">
                  <strong>
                    {visita.total !== null
                      ? visita.total.toLocaleString('es-MX', {
                        style: 'currency', currency: 'MXN',
                      })
                      /* Un total que ignora los renglones sin importe
                         parece completo y no lo está. */
                      : 'no se puede sumar'}
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {reparte && abierta && (
        <>
          <div className="campo" style={{ marginTop: 12 }}>
            <label htmlFor="tecnico">Le toca a</label>
            <select
              id="tecnico"
              value={tecnicoId}
              onChange={(e) => setTecnicoId(e.target.value)}
            >
              <option value="">Sin asignar</option>
              {fueraDeZona && (
                <option value={fueraDeZona.id}>
                  {fueraDeZona.nombre_completo} (de otra zona)
                </option>
              )}
              <OpcionesDeTecnico tecnicos={tecnicos} />
            </select>
            {visita.sucursal?.region && (
              <span className="ayuda">
                Los técnicos de {visita.sucursal.region.nombre}.
              </span>
            )}
          </div>

          {tecnicoId !== (visita.tecnico?.id ?? '') && (
            <button
              disabled={trabajando}
              onClick={() => { void hacer(() => api.post('/preventivo/asignar', {
                visitas: [{ visitaId: visita.id, tecnicoId: tecnicoId || null }],
              })); }}
            >
              Guardar el técnico
            </button>
          )}

          {/* El acuse.
              Se elige de una lista y no se teclea: el identificador de la
              orden es un UUID que nadie tiene a la vista, y pedirlo a mano
              era pedir un dato que no existe en la cabeza de nadie. La
              lista trae lo que la API va a aceptar —preventivas de esta
              sucursal, sin haber cerrado otra visita—, así que lo que se
              ofrece es exactamente lo que se puede guardar. */}
          <div className="campo" style={{ marginTop: 16 }}>
            <label htmlFor="orden">Orden preventiva que la cumplió</label>
            {visita.ordenTrabajo ? (
              /* Ya la tiene. Se enseña y no se vuelve a preguntar: que el
                 desplegable siguiera ahí convertía un dato resuelto en una
                 pregunta pendiente. Cambiarla es otra cosa —deshacer el
                 enlace— y vive en su propio botón. */
              <>
                <span className="mono" style={{ padding: '6px 0', display: 'block' }}>
                  {folioOT(visita.ordenTrabajo.folio)}
                </span>
                <span className="ayuda">
                  {visita.enlaceAutomatico
                    ? 'La enlazó la carga, por sucursal y mes. Si no es la que'
                      + ' corresponde, se deshace desde el botón de arriba.'
                    : 'Ya está enlazada a esta visita.'}
                </span>
              </>
            ) : candidatas === null ? <Cargando /> : candidatas.length === 0 ? (
              <div className="ayuda">
                Todavía no hay ninguna orden preventiva de esta sucursal sin
                usar. Si el técnico ya fue, acusa la visita con su OS: la
                orden se enlaza sola en cuanto se cargue.
              </div>
            ) : (
              <>
                <select id="orden" value={ordenId} onChange={(e) => setOrdenId(e.target.value)}>
                  <option value="">Todavía no llega su OT</option>
                  {candidatas.map((o) => (
                    <option key={o.id} value={o.id}>
                      {folioOT(o.folio)}
                      {o.noOsCliente ? ` · OS ${o.noOsCliente}` : ''}
                      {` · ${fechaCorta(o.creadaEn)}`}
                    </option>
                  ))}
                </select>
                <span className="ayuda">
                  Preventivas de esta sucursal que todavía no acusan otra
                  visita.
                </span>
              </>
            )}
          </div>

          {/*
            La OS del técnico que se adelantó.

            Es lo que APRISA describió: hay técnicos que saben que les toca
            su sucursal y van sin esperar la orden, y mandan su OS por
            fuera. Antes ese trabajo no tenía dónde registrarse hasta que
            llegara el folio, y para entonces el dato se contaba de
            memoria.

            No se pide cuando ya se eligió la orden: ahí el acuse ya está
            sostenido, y un campo más que nadie necesita llenar es un campo
            que la gente llena mal.
          */}
          {!ordenId && (
            <div className="campo" style={{ marginTop: 16 }}>
              <label htmlFor="noOs">Número de OS firmada</label>
              <input
                id="noOs"
                value={noOs}
                onChange={(e) => setNoOs(e.target.value)}
                placeholder="La que el técnico llevó firmada"
                maxLength={50}
              />
              <span className="ayuda">
                Con esto la visita queda acusada aunque su OT no haya
                llegado. Cuando se cargue, el sistema las junta y avisa.
              </span>
            </div>
          )}

          {/* Qué se hizo. De aquí sale la factura de la preventiva: cada
              servicio se guarda con el precio del día de la visita. */}
          <div className="campo" style={{ marginTop: 16 }}>
            <label>Servicios que se hicieron</label>
            {pactadosDeLaSucursal.length === 0 ? (
              <span className="ayuda">
                Esta sucursal no tiene servicios preventivos pactados. Se
                capturan en Administración → Precios del preventivo;
                mientras no los tenga, la visita se cierra pero no hay qué
                facturar.
              </span>
            ) : (
              <>
                {pactadosDeLaSucursal.map((s) => (
                  <div key={s.id} style={{ marginBottom: 8 }}>
                    <label style={{ display: 'block', fontWeight: 400 }}>
                      <input
                        type="checkbox"
                        checked={hechos.has(s.id)}
                        onChange={() => setHechos((antes) => {
                          const ahora = new Set(antes);
                          if (ahora.has(s.id)) ahora.delete(s.id);
                          else ahora.add(s.id);
                          return ahora;
                        })}
                      />{' '}
                      {s.codigo} · {s.nombre}
                    </label>

                    {/*
                      Sobre qué equipos se hizo.

                      Aquí y no arriba en la orden: una preventiva le da
                      servicio a la cámara, a la plancha y al horno en la
                      misma visita, y con un solo equipo en la orden no
                      había dónde apuntar los otros dos. Cuelga del
                      servicio para que se sepa qué se le hizo a cuál.

                      Uno por renglón y no un campo con comas: son
                      nombres que llevan números —«Cámara 2»— y las comas
                      se usan al describir. Sólo aparece si el servicio
                      está palomeado; si se despaloma, la API descarta
                      sus equipos.
                    */}
                    {hechos.has(s.id) && (
                      <textarea
                        rows={2}
                        value={equipos[s.id] ?? ''}
                        onChange={(e) => setEquipos((antes) => ({
                          ...antes, [s.id]: e.target.value,
                        }))}
                        placeholder="Equipos, uno por renglón"
                        style={{ marginLeft: 22, width: 'calc(100% - 22px)' }}
                      />
                    )}
                  </div>
                ))}
                <span className="ayuda">
                  Ninguno viene palomeado: marca sólo los que se hicieron,
                  porque cada uno es un renglón de la factura. En cada uno
                  puedes anotar los equipos que se atendieron —uno por
                  renglón—; el precio no cambia, es el pactado del servicio.
                </span>
              </>
            )}
          </div>

          <div className="controles">
            <button
              className="primario"
              disabled={(!ordenId && !noOs.trim()) || hechos.size === 0 || trabajando}
              onClick={() => { void hacer(() => api.post(`/preventivo/${visita.id}/realizada`, {
                ordenTrabajoId: ordenId || undefined,
                noOs: noOs.trim() || undefined,
                /**
                 * Lo que hizo el técnico va siempre, haya orden o no.
                 *
                 * Con orden se escribe en sus renglones, con precio. Sin
                 * ella se guarda en la visita y se copia cuando la OT
                 * llegue. Antes no se mandaba, y lo que alguien había
                 * despalomado se perdía: al llegar la orden se facturaban
                 * los tres servicios pactados aunque se hubieran hecho
                 * dos.
                 */
                servicios: [...hechos],
                /**
                 * Los equipos que se atendieron en cada servicio.
                 *
                 * Sólo van los servicios que quedaron palomeados: los
                 * demás la API los descarta de todas formas, y mandarlos
                 * sería pedirle que corrija lo que la pantalla ya sabe.
                 */
                equipos: [...hechos]
                  .map((servicioPreventivoId) => ({
                    servicioPreventivoId,
                    equipos: (equipos[servicioPreventivoId] ?? '')
                      .split('\n')
                      .map((t) => t.trim())
                      .filter((t) => t.length > 0)
                      .map((texto) => ({ texto })),
                  }))
                  .filter((g) => g.equipos.length > 0),
              })); }}
            >
              {visita.acuse?.etiqueta ?? 'Marcar realizada'}
            </button>
            <button disabled={trabajando} onClick={() => setNoSeHizo(true)}>
              No se hizo
            </button>
          </div>

          {/* Un botón desactivado tiene que decir qué le falta. Es el
              mismo defecto que ya se corrigió en «pedir por anticipado»:
              sin esto se ve como si la pantalla estuviera rota. */}
          {!ordenId && !noOs.trim() && (
            <span className="ayuda">
              Para marcarla realizada, elige su OT o escribe el número de
              OS que el técnico llevó firmada.
            </span>
          )}

          {/* El segundo motivo por el que el botón puede estar apagado,
              y desde que los servicios ya no vienen palomeados es el más
              frecuente. Decirlo importa: acusar la visita sin ninguno
              deja la orden sin nada que facturar. */}
          {(ordenId || noOs.trim()) && hechos.size === 0 && (
            <span className="ayuda">
              Falta marcar qué servicios se hicieron. Son los renglones de
              la factura: sin al menos uno no hay nada que cobrar.
            </span>
          )}
        </>
      )}

      {/*
        Rechazar, desde donde se ve el problema.

        Cancelar la orden ya se podía desde Órdenes, pero la sucursal
        repetida se descubre aquí: es en el programa donde se ve la misma
        tienda dos veces. Mandar a buscar el folio a otra pantalla para
        rechazarlo es la clase de viaje que acaba en que nadie lo hace.

        Es un solo acto: cancela la orden y la visita queda no realizada
        con el mismo motivo, en la misma transacción. Lo hace la API.
      */}
      {reparte && visita.ordenTrabajo && visita.estatus !== 'no_realizada' && (
        <div className="controles" style={{ marginTop: 12 }}>
          <button disabled={trabajando} onClick={() => setRechazando(true)}>
            Rechazar la orden
          </button>
          <span className="ayuda">
            {visita.otraOrdenEnElMes
              ? 'Esta sucursal trae más de una OT este mes. Rechaza la que sobre:'
                + ' se cancela la orden y la visita queda como no realizada.'
              : 'Cancela la orden y deja la visita como no realizada, con el'
                + ' motivo que escribas.'}
          </span>
        </div>
      )}

      {rechazando && visita.ordenTrabajo && (
        <DialogoMotivo
          titulo={`Rechazar la ${folioOT(visita.ordenTrabajo.folio)}`}
          descripcion={
            <>
              El servicio no se va a hacer. La orden se cancela y la visita
              queda como no realizada, con este motivo en las dos.
            </>
          }
          textoBoton="Rechazar la orden"
          motivo={motivo}
          setMotivo={setMotivo}
          trabajando={trabajando}
          onCerrar={() => setRechazando(false)}
          onConfirmar={() => {
            void hacer(() => api.post(`/ordenes/${visita.ordenTrabajo?.id}/transicion`, {
              hacia: 'cancelado',
              motivo,
            }));
          }}
        />
      )}

      {/*
        Cerrar la orden desde aquí, en un acto.

        Karin: «tengo que hacer un flujo para poder cerrar la orden desde
        el modulo de ordenes, como podemos eficientar esa flujo?»

        Eran tres pasos en Órdenes: asignar técnico, iniciar trabajo y
        cerrar. Los dos primeros no son decisiones en una preventiva —el
        técnico ya lo sabe la visita, e «iniciar» declara que empezó algo
        que ya terminó— y los camina la API sola, cada uno por el motor de
        estados. El tercero sí lo es: es lo que manda la orden a
        facturación, y por eso se sigue confirmando, con el texto a la
        vista.
      */}
      {cerrando && visita.ordenTrabajo && visita.cierrePropuesto && (
        <CerrarLaPreventiva
          ordenId={visita.ordenTrabajo.id}
          folio={visita.ordenTrabajo.folio}
          propuesto={visita.cierrePropuesto}
          noOs={visita.noOs}
          onCerrar={() => setCerrando(false)}
          onHecho={() => { setCerrando(false); onHecho(); }}
        />
      )}

      {/* La orden completa, sin salir de aquí.
          Sigue estando: cerrar es lo normal, pero a veces hay que mirar
          la bitácora, las evidencias o lo que impide cerrarla. */}
      {viendoOrden && visita.ordenTrabajo && (
        <FichaDeLaOrden
          id={visita.ordenTrabajo.id}
          tecnicos={tecnicos.map((t) => ({ id: t.id, nombre: t.nombre }))}
          onCerrar={() => setViendoOrden(false)}
          onCambio={onHecho}
        />
      )}

      {noSeHizo && (
        <DialogoMotivo
          titulo="La visita no se hizo"
          descripcion="Di por qué. Es lo que se revisa al armar el mes siguiente, para no arrastrar la misma sucursal sin darse cuenta."
          textoBoton="Guardar"
          motivo={motivo}
          setMotivo={setMotivo}
          trabajando={trabajando}
          error={null}
          onCerrar={() => setNoSeHizo(false)}
          onConfirmar={() => {
            void hacer(() => api.post(`/preventivo/${visita.id}/no-realizada`, { motivo }));
            setNoSeHizo(false);
          }}
        />
      )}
    </Dialogo>
  );
}
